/**
 * @fileoverview Announcement Banner Component
 * 
 * Displays active announcements at the top of the app.
 */

import { X, ExternalLink, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAnnouncements } from '@/hooks/useAnnouncements';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

const colorClasses: Record<string, string> = {
  primary: 'bg-primary text-primary-foreground',
  secondary: 'bg-secondary text-secondary-foreground',
  destructive: 'bg-destructive text-destructive-foreground',
  warning: 'bg-amber-500 text-white',
  success: 'bg-emerald-500 text-white',
  info: 'bg-sky-500 text-white',
};

export function AnnouncementBanner() {
  const { announcements, dismiss } = useAnnouncements();

  // Show only the first active announcement
  const announcement = announcements[0];

  if (!announcement) return null;

  const isExternal = announcement.link_url?.startsWith('http');
  const colorClass = colorClasses[announcement.bg_color] || colorClasses.primary;

  return (
    <div
      className={cn(
        'w-full px-4 py-2.5 flex items-center justify-center gap-2 text-sm relative',
        colorClass
      )}
    >
      <div className="flex items-center gap-2 flex-1 justify-center text-center">
        <span className="font-medium">{announcement.title}</span>
        {announcement.message && (
          <span className="hidden sm:inline opacity-90">- {announcement.message}</span>
        )}
        {announcement.link_url && (
          isExternal ? (
            <a
              href={announcement.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80 font-medium"
            >
              {announcement.link_text || 'Learn more'}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <Link
              to={announcement.link_url}
              className="inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80 font-medium"
            >
              {announcement.link_text || 'Learn more'}
              <ChevronRight className="h-3 w-3" />
            </Link>
          )
        )}
      </div>
      
      {announcement.is_dismissible && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 hover:bg-white/20 absolute right-2"
          onClick={() => dismiss(announcement.id)}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Dismiss</span>
        </Button>
      )}
    </div>
  );
}
